import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAddCryptocurrencyMutation, useGetContractDetailsMutation } from "../../redux/cryptocurrencyApi";

import { useTranslation } from "react-i18next";

function AddCryptocurrency({ show, onHide, networks, users }) {
    const { t } = useTranslation();
    const [addData, setAddData] = useState({ is_token: "yes" });
    const [addCryptocurrency] = useAddCryptocurrencyMutation();
    const [getContractDetails] = useGetContractDetailsMutation();

    const handleAddSave = async () => {
        if (!addData.coin_name || !addData.coin_symbol || !addData.network_id || !addData.contract_address || !addData.decimal_places || !addData.is_token || !addData.wallet_type || !addData.logo || !addData.is_swapable || !addData.transfer_fee_status || !addData.etoken_min_withdrawal_amount) {
            toast.error("All fields are required!");
            return;
        }

        if (addData.is_swapable === "yes") {
            if (!addData.swap_fee_cryptocurrency_id || !addData.swap_fee_type || !addData.swap_fee_amount) {
                toast.error("All swap fee fields are required!");
                return;
            }
        }

        if (addData.transfer_fee_status === "ON") {
            if (!addData.transfer_fee_cryptocurrency_id || !addData.transfer_fee_type || !addData.transfer_fee_amount) {
                toast.error("All transfer fee fields are required!");
                return;
            }
        }

        const formData = new FormData();
        formData.append("coin_name", addData.coin_name);
        formData.append("coin_symbol", addData.coin_symbol);
        formData.append("network_id", addData.network_id);
        formData.append("contract_address", addData.contract_address);
        formData.append("decimal_places", addData.decimal_places);
        formData.append("is_token", addData.is_token);
        formData.append("wallet_type", addData.wallet_type);
        formData.append("logo", addData.logo);
        formData.append("etoken_min_withdrawal_amount", addData.etoken_min_withdrawal_amount);
        formData.append("is_swapable", addData.is_swapable);
        if (addData.is_swapable === "yes") {
            formData.append("swap_fee_cryptocurrency_id", addData.swap_fee_cryptocurrency_id);
            formData.append("swap_fee_type", addData.swap_fee_type);
            formData.append("swap_fee_amount", addData.swap_fee_amount);
        }
        formData.append("transfer_fee_status", addData.transfer_fee_status);
        if (addData.transfer_fee_status === "ON") {
            formData.append("transfer_fee_cryptocurrency_id", addData.transfer_fee_cryptocurrency_id);
            formData.append("transfer_fee_type", addData.transfer_fee_type);
            formData.append("transfer_fee_amount", addData.transfer_fee_amount);
        }

        await addCryptocurrency(formData);
        onHide();
        setAddData({ is_token: "yes" });
    };

    const handleContractAddressBlur = async () => {
        if (addData.network_id && addData.contract_address) {
            try {
                const response = await getContractDetails({
                    network_id: addData.network_id,
                    contract_address: addData.contract_address,
                }).unwrap();

                if (response.status) {
                    setAddData((prev) => ({
                        ...prev,
                        coin_name: response.data.name,
                        coin_symbol: response.data.symbol,
                        decimal_places: response.data.decimals,
                    }));
                    toast.success("Contract details fetched successfully!");
                } else {
                    toast.error(response.message || "Failed to fetch contract details");
                }
            } catch (error) {
                toast.error("Error fetching contract details");
                console.error("Error fetching contract details:", error);
            }
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{t('add_cryptocurrency')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('select_network')}</Form.Label>
                        <Form.Select
                            value={addData.network_id || ""}
                            onChange={(e) => setAddData({ ...addData, network_id: e.target.value })}
                        >
                            <option value="">{t('select_network')}</option>
                            {networks.map((network, index) => (
                                <option key={index} value={network.id}>
                                    {network.name} ({network.symbol})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('contract_address')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={addData.contract_address || ""}
                            onChange={(e) => setAddData({ ...addData, contract_address: e.target.value })}
                            onBlur={handleContractAddressBlur}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('coin_name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={addData.coin_name || ""}
                            onChange={(e) => setAddData({ ...addData, coin_name: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('coin_symbol')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={addData.coin_symbol || ""}
                            onChange={(e) => setAddData({ ...addData, coin_symbol: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('decimal_places')}</Form.Label>
                        <Form.Control
                            type="number"
                            value={addData.decimal_places || ""}
                            onChange={(e) => setAddData({ ...addData, decimal_places: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('is_token')}</Form.Label>
                        <Form.Select
                            value="yes"
                            disabled
                        >
                            <option value="yes">{t('yes')}</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('wallet_type')}</Form.Label>
                        <Form.Select
                            value={addData.wallet_type || ""}
                            onChange={(e) => setAddData({ ...addData, wallet_type: e.target.value })}
                        >
                            <option value="">{t('select')}</option>
                            {[...new Set(networks.map(network => network.wallet_type).filter(Boolean))].map((type, index) => (
                                <option key={index} value={type}>
                                    {type}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('logo')}</Form.Label>
                        <Form.Control
                            type="file"
                            onChange={(e) => setAddData({ ...addData, logo: e.target.files[0] })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('etoken_min_withdrawal_amount')}</Form.Label>
                        <Form.Control
                            type="number"
                            value={addData.etoken_min_withdrawal_amount || ""}
                            onChange={(e) => setAddData({ ...addData, etoken_min_withdrawal_amount: e.target.value })}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('is_swapable')}</Form.Label>
                        <Form.Select
                            value={addData.is_swapable || ""}
                            onChange={(e) => setAddData({ ...addData, is_swapable: e.target.value })}
                        >
                            <option value="">{t('select')}</option>
                            <option value="yes">{t('yes')}</option>
                            <option value="no">{t('no')}</option>
                        </Form.Select>
                    </Form.Group>
                    {addData.is_swapable === "yes" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('swap_fee_cryptocurrency')}</Form.Label>
                                <Form.Select
                                    value={addData.swap_fee_cryptocurrency_id || ""}
                                    onChange={(e) => setAddData({ ...addData, swap_fee_cryptocurrency_id: e.target.value })}
                                >
                                    <option value="">{t('select_cryptocurrency')}</option>
                                    {users.map((crypto, index) => (
                                        <option key={index} value={crypto.id}>
                                            {crypto.coin_name} ({crypto.coin_symbol}) - ({crypto.symbol})
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('swap_fee_type')}</Form.Label>
                                <Form.Select
                                    value={addData.swap_fee_type || ""}
                                    onChange={(e) => setAddData({ ...addData, swap_fee_type: e.target.value })}
                                >
                                    <option value="">{t('select')}</option>
                                    <option value="fixed">{t('fixed')}</option>
                                    <option value="percentage">{t('percentage')}</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('swap_fee_amount')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={addData.swap_fee_amount || ""}
                                    onChange={(e) => setAddData({ ...addData, swap_fee_amount: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>{t('transfer_fee_status')}</Form.Label>
                        <Form.Select
                            value={addData.transfer_fee_status || ""}
                            onChange={(e) => setAddData({ ...addData, transfer_fee_status: e.target.value })}
                        >
                            <option value="">{t('select')}</option>
                            <option value="ON">ON</option>
                            <option value="OFF">OFF</option>
                        </Form.Select>
                    </Form.Group>
                    {addData.transfer_fee_status === "ON" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('transfer_fee_cryptocurrency')}</Form.Label>
                                <Form.Select
                                    value={addData.transfer_fee_cryptocurrency_id || ""}
                                    onChange={(e) => setAddData({ ...addData, transfer_fee_cryptocurrency_id: e.target.value })}
                                >
                                    <option value="">{t('select_cryptocurrency')}</option>
                                    {users.map((crypto, index) => (
                                        <option key={index} value={crypto.id}>
                                            {crypto.coin_name} ({crypto.coin_symbol})- ({crypto.symbol})
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('transfer_fee_type')}</Form.Label>
                                <Form.Select
                                    value={addData.transfer_fee_type || ""}
                                    onChange={(e) => setAddData({ ...addData, transfer_fee_type: e.target.value })}
                                >
                                    <option value="">{t('select')}</option>
                                    <option value="fixed">{t('fixed')}</option>
                                    <option value="percentage">{t('percentage')}</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('transfer_fee_amount')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={addData.transfer_fee_amount || ""}
                                    onChange={(e) => setAddData({ ...addData, transfer_fee_amount: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('close')}
                </Button>
                <Button variant="primary" onClick={handleAddSave}>
                    {t('add_cryptocurrency')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default AddCryptocurrency;
